/**
 * @fileoverview AR-805 — school Google Workspace connection state + actions
 * over the SchoolConnectionPort.
 *
 * Connect/reconnect is the backend's 2-step OAuth flow: `connect()` sends the
 * browser to Google (full-page redirect) and Google returns to the SAME page
 * with `?code=`; the page then calls `completeConnect(code)`. The redirect URI
 * must be byte-identical in both steps — both derive it from `redirectUri()`.
 */

import { useCallback, useEffect, useState } from 'react'

import { getClassroomAdapters } from '../provider'
import { isClassroomPortError, SchoolConnection, SchoolConnectionStatus } from '../types'
import { useClassroomIntegrationEnabled } from './useClassroomIntegrationEnabled'
import { useRefreshOnFocus } from './useRefreshOnFocus'

/** `unauthorized` = the profile cannot manage the integration — hide the UI. */
export type ConnectionUiStatus = 'loading' | 'unauthorized' | SchoolConnectionStatus

/** Current page without query/hash — must match the Google Console allowlist. */
function classroomRedirectUri(): string {
  return `${window.location.origin}${window.location.pathname}`
}

export interface ClassroomConnectionState {
  status: ConnectionUiStatus
  info: SchoolConnection | null
  connected: boolean
  /**
   * True from the click until the browser leaves for Google. The button must
   * show it: the authorization URL request takes a moment and, without
   * feedback, the button looks dead (Apple HIG: acknowledge every action).
   */
  connecting: boolean
  /** True while the OAuth code is being exchanged on the callback. */
  completing: boolean
  disconnecting: boolean
  refresh: () => Promise<void>
  /** Redirects to Google; resolves only if the redirect could not start. */
  connect: () => Promise<boolean>
  completeConnect: (code: string) => Promise<boolean>
  disconnect: () => Promise<boolean>
}

export function useClassroomConnection(): ClassroomConnectionState {
  const { connection } = getClassroomAdapters()
  // No request at all outside Arc (or while the feature flags load).
  const { enabled, loading: flagsLoading } = useClassroomIntegrationEnabled()
  const [status, setStatus] = useState<ConnectionUiStatus>('loading')
  const [info, setInfo] = useState<SchoolConnection | null>(null)
  const [connecting, setConnecting] = useState(false)
  const [completing, setCompleting] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)

  const refresh = useCallback(async () => {
    try {
      const result = await connection.getConnection()
      setInfo(result)
      setStatus(result.status)
    } catch (error) {
      setInfo(null)
      // 401 means "not your integration": the surfaces hide themselves rather
      // than offering a Connect button that would always fail. Anything else
      // (endpoint missing on an older backend, network) reads as disconnected.
      setStatus(isClassroomPortError(error) && error.code === 'unauthorized' ? 'unauthorized' : 'disconnected')
    }
  }, [connection])

  useEffect(() => {
    // Outside Arc there is no integration to show: resolve to a terminal state
    // so the card hides itself instead of sitting on the skeleton forever
    // (the backend answers 200 for any school — the gate is ours).
    if (!enabled) {
      if (!flagsLoading) setStatus('unauthorized')
      return
    }
    let cancelled = false
    connection
      .getConnection()
      .then(result => {
        if (cancelled) return
        setInfo(result)
        setStatus(result.status)
      })
      .catch(error => {
        if (cancelled) return
        setInfo(null)
        setStatus(
          isClassroomPortError(error) && error.code === 'unauthorized' ? 'unauthorized' : 'disconnected',
        )
      })
    return () => {
      cancelled = true
    }
  }, [enabled, flagsLoading, connection])

  // The manager consents on Google and comes back — reflect it without a reload.
  useRefreshOnFocus(refresh, enabled)

  const connect = useCallback(async () => {
    setConnecting(true)
    try {
      await connection.startConnect(classroomRedirectUri())
      // On the real adapter the browser is navigating away at this point, so
      // `connecting` stays true until the page unloads (no flash back to the
      // idle button). The mock resolves immediately — refresh and clear.
      await refresh()
      setConnecting(false)
      return true
    } catch {
      setConnecting(false)
      return false
    }
  }, [connection, refresh])

  const completeConnect = useCallback(
    async (code: string) => {
      setCompleting(true)
      try {
        const result = await connection.completeConnect(code, classroomRedirectUri())
        setInfo(result)
        setStatus(result.status)
        return true
      } catch {
        await refresh()
        return false
      } finally {
        setCompleting(false)
      }
    },
    [connection, refresh],
  )

  const disconnect = useCallback(async () => {
    setDisconnecting(true)
    try {
      await connection.disconnect()
      await refresh()
      return true
    } catch {
      return false
    } finally {
      setDisconnecting(false)
    }
  }, [connection, refresh])

  return {
    status,
    info,
    connected: status === 'connected',
    connecting,
    completing,
    disconnecting,
    refresh,
    connect,
    completeConnect,
    disconnect,
  }
}
